import axios, { AxiosInstance, AxiosResponse } from 'axios'
import Cookies from 'js-cookie'
import { message } from 'antd'

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      Cookies.remove('auth_token')
      window.location.href = '/admin/login'
    } else if (error.response?.status >= 500) {
      message.error('Server error occurred. Please try again later.')
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: async (email: string, password: string, twoFactorToken?: string, backupCode?: string) => {
    const response = await api.post('/auth/login', { 
      email, 
      password,
      ...(twoFactorToken && { twoFactorToken }),
      ...(backupCode && { backupCode })
    })
    return response.data
  },
  
  logout: async () => {
    const response = await api.post('/auth/logout')
    return response.data
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/me')
    return response.data.user
  },
  
  refreshToken: async () => {
    const response = await api.post('/auth/refresh')
    return response.data
  },
  
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.put('/auth/change-password', {
      currentPassword,
      newPassword,
    })
    return response.data
  },

  // 2FA methods
  setup2FA: async () => {
    const response = await api.post('/auth/2fa/setup')
    return response.data
  },

  enable2FA: async (secret: string, token: string) => {
    const response = await api.post('/auth/2fa/enable', { secret, token })
    return response.data
  },

  disable2FA: async (currentPassword: string, twoFactorToken?: string, backupCode?: string) => {
    const response = await api.post('/auth/2fa/disable', {
      currentPassword,
      ...(twoFactorToken && { twoFactorToken }),
      ...(backupCode && { backupCode })
    })
    return response.data
  },

  regenerateBackupCodes: async (currentPassword: string, twoFactorToken?: string, backupCode?: string) => {
    const response = await api.post('/auth/2fa/regenerate-backup-codes', {
      currentPassword,
      ...(twoFactorToken && { twoFactorToken }),
      ...(backupCode && { backupCode })
    })
    return response.data
  },
}

// Categories API
export const categoriesApi = {
  getAll: async (params?: any) => {
    const response = await api.get('/categories', { params })
    return response.data
  },
  
  getById: async (id: string) => {
    const response = await api.get(`/categories/${id}`)
    return response.data
  },
  
  getAdminAll: async () => {
    const response = await api.get('/categories/admin/all')
    return response.data
  },
  
  create: async (data: any) => {
    console.log('API: Sending POST to /categories with data:', data);
    const response = await api.post('/categories', data)
    console.log('API: Response received:', response.data);
    return response.data
  },
  
  update: async (id: string, data: any) => {
    const response = await api.put(`/categories/${id}`, data)
    return response.data
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/categories/${id}`)
    return response.data
  },
  
  updateProductCounts: async () => {
    const response = await api.post('/categories/update-counts')
    return response.data
  }
}

// Products API
export const productsApi = {
  getAll: async (params?: any) => {
    const response = await api.get('/products', { params })
    return response.data
  },
  
  getAdminAll: async (params?: any) => {
    const response = await api.get('/products/admin/all', { params })
    if (process.env.NODE_ENV === 'development') {
      try {
        const first = response.data?.products?.[0]
        // eslint-disable-next-line no-console
        console.log('[API] /products/admin/all FIRST description', {
          id: first?._id,
          en: first?.description?.en,
          zh: first?.description?.zh,
        })
      } catch {}
    }
    return response.data
  },
  
  getById: async (id: string) => {
    const response = await api.get(`/products/id/${id}`)
    return response.data
  },
  
  create: async (data: any) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[API] /products create payload.description', {
        en: data?.description?.en,
        zh: data?.description?.zh,
      })
    }
    const response = await api.post('/products', data)
    return response.data
  },
  
  update: async (id: string, data: any) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[API] /products update payload.description', {
        en: data?.description?.en,
        zh: data?.description?.zh,
      })
    }
    const response = await api.put(`/products/${id}`, data)
    return response.data
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/products/${id}`)
    return response.data
  },
  
  toggleActive: async (id: string) => {
    const response = await api.patch(`/products/${id}/toggle-active`)
    return response.data
  },
  
  toggleFeatured: async (id: string) => {
    const response = await api.patch(`/products/${id}/toggle-featured`)
    return response.data
  },
  
  toggleNew: async (id: string) => {
    const response = await api.patch(`/products/${id}/toggle-new`)
    return response.data
  },
}

// Articles API
export const articlesApi = {
  getAll: async (params?: any) => {
    const response = await api.get('/articles', { params })
    return response.data
  },
  
  getById: async (id: string) => {
    const response = await api.get(`/articles/id/${id}`)
    return response.data
  },
  
  getBySlug: async (slug: string) => {
    const response = await api.get(`/articles/slug/${slug}`)
    return response.data
  },
  
  create: async (data: any) => {
    const response = await api.post('/articles', data)
    return response.data
  },
  
  update: async (id: string, data: any) => {
    const response = await api.put(`/articles/${id}`, data)
    return response.data
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/articles/${id}`)
    return response.data
  },
  
  publish: async (id: string) => {
    const response = await api.put(`/articles/${id}/publish`)
    return response.data
  },
  
  unpublish: async (id: string) => {
    const response = await api.put(`/articles/${id}/unpublish`)
    return response.data
  },

  togglePublished: async (id: string) => {
    const response = await api.patch(`/articles/${id}/toggle-published`)
    return response.data
  },

  toggleFeatured: async (id: string) => {
    const response = await api.patch(`/articles/${id}/toggle-featured`)
    return response.data
  },
}

// Stores API
export const storesApi = {
  getAll: async (params?: any) => {
    const response = await api.get('/stores', { params })
    return response.data
  },
  
  getById: async (id: string) => {
    const response = await api.get(`/stores/${id}`)
    return response.data
  },
  
  getNearby: async (lat: number, lng: number, maxDistance?: number) => {
    const response = await api.get('/stores/nearby', {
      params: { lat, lng, maxDistance }
    })
    return response.data
  },
  
  create: async (data: any) => {
    const response = await api.post('/stores', data)
    return response.data
  },
  
  update: async (id: string, data: any) => {
    const response = await api.put(`/stores/${id}`, data)
    return response.data
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/stores/${id}`)
    return response.data
  },
}

// Contact API
export const contactApi = {
  submit: async (data: any) => {
    const response = await api.post('/contact', data)
    return response.data
  },
  
  getAll: async (params?: any) => {
    const response = await api.get('/contact', { params })
    return response.data
  },
  
  getById: async (id: string) => {
    const response = await api.get(`/contact/${id}`)
    return response.data
  },
  
  updateStatus: async (id: string, status: string, notes?: string) => {
    const response = await api.put(`/contact/${id}/status`, { status, notes })
    return response.data
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/contact/${id}`)
    return response.data
  },
}

// Upload API
export const uploadApi = {
  uploadImage: async (file: File) => {
    const formData = new FormData()
    formData.append('image', file)
    const response = await api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },
  
  uploadDocument: async (file: File) => {
    const formData = new FormData()
    formData.append('document', file)
    const response = await api.post('/upload/document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },
  
  uploadVideo: async (file: File) => {
    const formData = new FormData()
    formData.append('video', file)
    const response = await api.post('/upload/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },
  
  deleteFile: async (fileName: string, type: string) => {
    const response = await api.delete('/upload/file', {
      data: { fileName, type }
    })
    return response.data
  },
}

// Admin API
export const adminApi = {
  seedData: async () => {
    const response = await api.post('/admin/seed')
    return response.data
  },
  
  getSystemInfo: async () => {
    const response = await api.get('/admin/system')
    return response.data
  },
}

// Site Settings API
export const siteSettingsApi = {
  // Public endpoint for getting site settings (used by public pages)
  getPublicSettings: async () => {
    const response = await api.get('/site-settings/public')
    return response.data
  },
  
  // Admin endpoints for managing site settings
  getSettings: async () => {
    const response = await api.get('/site-settings')
    return response.data
  },
  
  updateSettings: async (data: any) => {
    const response = await api.put('/site-settings', data)
    try { localStorage.setItem('site_settings_updated', String(Date.now())); } catch (_) {}
    try { const bc = new BroadcastChannel('site-settings'); bc.postMessage('updated'); bc.close(); } catch (_) {}
    return response.data
  },
  
  updateHero: async (data: any) => {
    const response = await api.put('/site-settings/hero', data)
    try { localStorage.setItem('site_settings_updated', String(Date.now())); } catch (_) {}
    try { const bc = new BroadcastChannel('site-settings'); bc.postMessage('updated'); bc.close(); } catch (_) {}
    return response.data
  },
  
  updateContact: async (data: any) => {
    const response = await api.put('/site-settings/contact', data)
    try { localStorage.setItem('site_settings_updated', String(Date.now())); } catch (_) {}
    try { const bc = new BroadcastChannel('site-settings'); bc.postMessage('updated'); bc.close(); } catch (_) {}
    return response.data
  },
  
  updateLogo: async (data: any) => {
    const response = await api.put('/site-settings/logo', data)
    try { localStorage.setItem('site_settings_updated', String(Date.now())); } catch (_) {}
    try { const bc = new BroadcastChannel('site-settings'); bc.postMessage('updated'); bc.close(); } catch (_) {}
    return response.data
  },
  
  updatePopupNotice: async (data: any) => {
    const response = await api.put('/site-settings/popup-notice', data)
    try { localStorage.setItem('site_settings_updated', String(Date.now())); } catch (_) {}
    try { const bc = new BroadcastChannel('site-settings'); bc.postMessage('updated'); bc.close(); } catch (_) {}
    return response.data
  },
  
  updatePageContent: async (data: any) => {
    const response = await api.put('/site-settings/pages', data)
    // Broadcast ONLY after explicit save; callers decide when to call this.
    try { localStorage.setItem('site_settings_updated', String(Date.now())); } catch (_) {}
    try { const bc = new BroadcastChannel('site-settings'); bc.postMessage('updated'); bc.close(); } catch (_) {}
    return response.data
  },
  
  seedSettings: async () => {
    const response = await api.post('/site-settings/seed')
    return response.data
  },
}

// Service Content API
export const serviceContentApi = {
  // Public endpoint for getting service content
  getPublicContent: async () => {
    const response = await api.get('/service-content/public')
    return response.data
  },
  
  // Admin endpoints for managing service content
  getContent: async () => {
    const response = await api.get('/service-content')
    return response.data
  },
  
  updateContent: async (data: any) => {
    const response = await api.put('/service-content', data)
    return response.data
  },
  
  // Section management
  addSection: async (data: any) => {
    const response = await api.post('/service-content/sections', data)
    return response.data
  },
  
  updateSection: async (id: string, data: any) => {
    const response = await api.put(`/service-content/sections/${id}`, data)
    return response.data
  },
  
  deleteSection: async (id: string) => {
    const response = await api.delete(`/service-content/sections/${id}`)
    return response.data
  },
  
  // Contact management
  addContact: async (data: any) => {
    const response = await api.post('/service-content/contacts', data)
    return response.data
  },
  
  updateContact: async (id: string, data: any) => {
    const response = await api.put(`/service-content/contacts/${id}`, data)
    return response.data
  },
  
  deleteContact: async (id: string) => {
    const response = await api.delete(`/service-content/contacts/${id}`)
    return response.data
  }
}

// About Content API
export const aboutContentApi = {
  // Public endpoint for getting about content
  getPublicContent: async () => {
    const response = await api.get('/about-content/public')
    return response.data
  },
  
  // Admin endpoints for managing about content
  getContent: async () => {
    const response = await api.get('/about-content')
    return response.data
  },
  
  updateContent: async (data: any) => {
    const response = await api.put('/about-content', data)
    return response.data
  },
  
  // Values management
  addValue: async (data: any) => {
    const response = await api.post('/about-content/values', data)
    return response.data
  },
  
  updateValue: async (id: string, data: any) => {
    const response = await api.put(`/about-content/values/${id}`, data)
    return response.data
  },
  
  deleteValue: async (id: string) => {
    const response = await api.delete(`/about-content/values/${id}`)
    return response.data
  },
  
  // Achievements management
  addAchievement: async (data: any) => {
    const response = await api.post('/about-content/achievements', data)
    return response.data
  },
  
  updateAchievement: async (id: string, data: any) => {
    const response = await api.put(`/about-content/achievements/${id}`, data)
    return response.data
  },
  
  deleteAchievement: async (id: string) => {
    const response = await api.delete(`/about-content/achievements/${id}`)
    return response.data
  }
}

export default api 